import React, { useEffect, useRef, useState } from "react";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { useTranslation } from "react-i18next";
import { taskRequest } from "./api";
import useBoardBackground from "./useBoardBackground";

export default function BoardBackgroundDialog({ board, onClose, onSaved }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(board.backgroundImage ? "image" : board.backgroundColor ? "color" : "default");
  const [color, setColor] = useState(board.backgroundColor || "#e8eef4");
  const [file, setFile] = useState(null);
  const [localUrl, setLocalUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const mutation = useRef(false);
  const background = useBoardBackground(board);
  useEffect(() => {
    if (!file) { setLocalUrl(null); return; }
    const url = URL.createObjectURL(file);
    setLocalUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  const save = async (event) => {
    event.preventDefault();
    if (mutation.current) return;
    mutation.current = true;
    setBusy(true);
    setError("");
    let savedBoard;
    try {
      let body = { boardId: board.id, mode, ...(mode === "color" ? { backgroundColor: color } : {}) };
      if (mode === "image" && file) {
        body = new FormData();
        body.append("boardId", board.id);
        body.append("mode", mode);
        body.append("image", file);
      }
      const result = await taskRequest("save_task_board_background", body);
      savedBoard = result.board;
    } catch (err) { setError(err.message); }
    finally { mutation.current = false; setBusy(false); }
    if (savedBoard) { onSaved(savedBoard); onClose(); }
  };
  const style = mode === "image" ? {
    ...background.style,
    ...(localUrl ? { backgroundImage: `url(${JSON.stringify(localUrl)})` } : {}),
  } : { backgroundColor: mode === "color" ? color : "#eeeeee" };
  return (
    <Dialog open onClose={() => { if (!busy) onClose(); }} fullWidth maxWidth="sm" aria-labelledby="board-background-title">
      <form onSubmit={save}>
        <DialogTitle id="board-background-title">{t("tasks.boardBackground")}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error">{error}</Alert>}
          {mode === "image" && !file && background.error && <Alert severity="warning">{t("mediaPreview.failed")}</Alert>}
          <TextField select fullWidth margin="normal" id="board-background-type" label={t("tasks.backgroundType")}
            value={mode} disabled={busy} onChange={(event) => setMode(event.target.value)} SelectProps={{ native: true }}>
            {["default", "color", "image"].map((value) => <option key={value} value={value}>{t(`tasks.boardBackgroundTypes.${value}`)}</option>)}
          </TextField>
          {mode === "color" && <TextField type="color" fullWidth margin="normal" id="board-background-color"
            label={t("tasks.boardBackgroundColor")} value={color} disabled={busy} InputLabelProps={{ shrink: true }}
            onChange={(event) => setColor(event.target.value)} />}
          {mode === "image" && <>
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" aria-label={t("tasks.boardBackgroundImage")} disabled={busy}
              onChange={(event) => {
                const selected = event.target.files[0];
                if (selected && selected.size > 10 * 1024 * 1024) {
                  setError(t("tasks.boardBackgroundImageHelp"));
                  event.target.value = "";
                  setFile(null);
                } else { setError(""); setFile(selected || null); }
              }} />
            <Typography variant="caption" display="block">{t("tasks.boardBackgroundImageHelp")}</Typography>
          </>}
          <div aria-label={t("tasks.backgroundPreview")} style={{ ...style, height: 180, borderRadius: 8, marginTop: 16, padding: 16 }}>
            <Typography style={{ display: "inline-block", padding: "8px 12px", background: "white", color: "black", borderRadius: 4 }}>{board.name}</Typography>
          </div>
        </DialogContent>
        <DialogActions>
          {busy && <CircularProgress size={20} />}
          <Button disabled={busy} onClick={onClose}>{t("tasks.cancel")}</Button>
          <Button type="submit" color="primary" disabled={busy || (mode === "image" && !file && !board.backgroundImage)}>{t("tasks.save")}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
